
import { Conversation, ConversationsResponse, FeedbackRequest, QueryRequest, QueryResponse } from 'models/ai-chat/ai-chat-response';
import { ApiServiceBase } from './base/api-service-base';
import { FactDimTables, TablePreviewInfo } from 'models/ai-chat/data-explorer';
import { TableRequest, TableTransactionResponse } from 'models/ai-chat/table-pagination';
import { FilterRequest, FilterItem } from 'models/ai-chat/filter-request';

export interface Project {
    projectId: string;
    projectName: string;
    userId?: string;
    createdAt?: string;
}

export interface ProjectListResponse {
    projects: Project[];
    totalCount: number;
}

export interface ConversationProjectRequest {
    projectId: string;
    conversationId: string;
}

export interface ConversationProjectResponse {
    success: boolean;
    message: string;
}

const baseEndpoint = 'aianalyst';

export interface FilterColumnData {
  columnname?: string;
  field_name?: string;
  data_type: string;
  suggestion: string;
  values: string[];
  isDropdown?: boolean;
  operators?: FilterOperator[];
}

export interface FilterOperator {
  value: string;
  label: string;
}

export interface AvailableFilterColumnsResponse {
  table_name: string;
  filter_columns: {
    [key: string]: {
      data_type: string;
      suggestion: string;
      operators: FilterOperator[];
      values: (string | null)[];
    };
  };
}

export interface IAIChatApiService {
    sendQueryRequest(request: QueryRequest): Promise<QueryResponse>;
    getConversation(page?: number, pageSize?: number, filterTerm?: string): Promise<ConversationsResponse>;
    getConversationsByProjectId(projectId: string, page?: number, pageSize?: number): Promise<ConversationsResponse>;
    getConversationById(id: string): Promise<Conversation>;
    createConversation( title?: string): Promise<Conversation>;
    addMessageFeedback(request:FeedbackRequest): Promise<any>;
    sendMessageQueryRequest(request: QueryRequest): Promise<QueryResponse>;
    getThreadMessagesByMessageId(id: string): Promise<QueryResponse[]>;
    getTableList(): Promise<FactDimTables[]>;
    getTablePreview(table_name:string): Promise<TablePreviewInfo>;
    getTablePaginationData(request:TableRequest): Promise<TableTransactionResponse>;
    GetSqlFiltersForField(fieldName: string, customerId?: string): Promise<FilterColumnData>;
    GetSqlFiltersForMultipleFields(fieldNames: string[], customerId?: string): Promise<FilterColumnData[]>;
    downloadTableAsPDF(messageId: string, startDate?: string, toDate?: string, filterData?: FilterItem[]): Promise<Blob>;
    sendFilterQuery(request: FilterRequest): Promise<QueryResponse>;
    sendFilterQueryStream(request: FilterRequest, onChunk: (chunk: string) => void, onError?: (error: Error) => void, onComplete?: () => void): Promise<void>;
    sendExplanationQueryRequest(request: QueryRequest, onChunk: (chunk: string) => void, onError?: (error: Error) => void, onComplete?: () => void): Promise<void>;
    createProject(projectName: string): Promise<Project>;
    getProjectList(): Promise<ProjectListResponse>;
    assignConversationToProject(request: ConversationProjectRequest): Promise<ConversationProjectResponse>;
    getUnderlyingData(conversationId: string, messageId: string, dimensionName?: string): Promise<any>;
    deleteProject(projectId: string): Promise<any>;
    renameProject(projectId: string, projectName: string): Promise<any>;
    deleteConversation(conversationId: string): Promise<any>;
    getAvailableFilterColumns(messageId: string): Promise<AvailableFilterColumnsResponse>;
}

export class AIChatApiService extends ApiServiceBase implements IAIChatApiService {

    public async sendQueryRequest(request: QueryRequest): Promise<QueryResponse> {
        return await this.PostToApi<any>(`${baseEndpoint}/query`,request);
    }

    public async sendMessageQueryRequest(request: QueryRequest): Promise<QueryResponse> {
        return await this.PostToApi<any>(`${baseEndpoint}/message-query`,request);
    }


    public async createConversation( title?: string): Promise<Conversation> {
        return await this.PostToApi<any>(`${baseEndpoint}/conversations`, { title });
    }

    public async getConversation(page: number = 1, pageSize: number = 10, filterTerm?: string): Promise<ConversationsResponse> {
        const requestBody = {
            filter_term: filterTerm || null,
            limit: pageSize,
            offset: (page - 1) * pageSize
        };
        
        return await this.PostToApi<any>(`${baseEndpoint}/conversations/user`, requestBody);
    }

    public async getConversationsByProjectId(projectId: string, page: number = 1, pageSize: number = 10): Promise<ConversationsResponse> {
        const queryParams = new URLSearchParams({
            limit: pageSize.toString(),
            offset: ((page - 1) * pageSize).toString()
        });
        
        return await this.GetFromApi<any>(`${baseEndpoint}/projects/conversations/${projectId}?${queryParams}`);
    }

    public async getConversationById(id: string): Promise<Conversation> {
        return await this.GetFromApi<any>(`${baseEndpoint}/conversations/${id}`);
    }
    
    public async addMessageFeedback(request:FeedbackRequest): Promise<any> {
        return await this.PostToApi<any>(`${baseEndpoint}/feedback`,request);
    }

    
    public async getThreadMessagesByMessageId(id: string): Promise<QueryResponse[]> {
        return await this.GetFromApi<any>(`${baseEndpoint}/threadmessages/${id}`);
    }

    public async getTableList(): Promise<FactDimTables[]> {
        return await this.GetFromApi<any>(`${baseEndpoint}/table`);
    }

    public async getTablePreview(table_name:string): Promise<TablePreviewInfo> {
        return await this.GetFromApi<any>(`${baseEndpoint}/table-preview?tableName=${table_name}`);
    }

    public async getTablePaginationData(request: TableRequest): Promise<TableTransactionResponse> {
        return await this.PostToApi<any>(`${baseEndpoint}/pagination`,request);
    }

    public async GetSqlFiltersForField(fieldName: string, customerId?: string): Promise<FilterColumnData> {
        const endpoint = customerId 
            ? `${baseEndpoint}/filter-columns/${fieldName}?customerId=${customerId}` 
            : `${baseEndpoint}/filter-columns/${fieldName}`;
        return await this.GetFromApi<FilterColumnData>(endpoint);
    }

    public async GetSqlFiltersForMultipleFields(fieldNames: string[], customerId?: string): Promise<FilterColumnData[]> {
        const requestBody = {
            fieldNames: fieldNames,
            customerId: customerId
        };
        return await this.PostToApi<FilterColumnData[]>(`${baseEndpoint}/filter-columns/multiple`, requestBody);
    }

    public async downloadTableAsPDF(messageId: string, startDate?: string, toDate?: string, filterData?: FilterItem[]): Promise<Blob> {
        const requestBody: { message_id: string; startDate?: string; toDate?: string; filter_data?: FilterItem[] } = {
            message_id: messageId
        };
        
        if (startDate) {
            requestBody.startDate = startDate;
        }
        
        if (toDate) {
            requestBody.toDate = toDate;
        }
        
        if (filterData && filterData.length > 0) {
            requestBody.filter_data = filterData;
        }

        try {
            const blob = await this.PostToApi<Blob>(`${baseEndpoint}/download-pdf`, requestBody, 'blob');
            return blob;
        } catch (error) {
            try {
                const base64Response = await this.PostToApi<{ data: string; filename?: string }>(`${baseEndpoint}/download-pdf`, requestBody, 'json');
                if (base64Response.data) {
                    const byteCharacters = atob(base64Response.data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    return new Blob([byteArray], { type: 'application/pdf' });
                } else {
                    throw new Error('No data received from PDF download endpoint');
                }
            } catch (base64Error) {
                throw new Error(`PDF download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    }

    public async sendFilterQuery(request: FilterRequest): Promise<QueryResponse> {
        return await this.PostToApi<any>(`${baseEndpoint}/filter-query`, request);
    }

    public async sendFilterQueryStream(
        request: FilterRequest, 
        onChunk: (chunk: string) => void, 
        onError?: (error: Error) => void, 
        onComplete?: () => void
    ): Promise<void> {
        return await this.PostStreamToApi(
            `${baseEndpoint}/filter-query`, 
            request, 
            onChunk, 
            onError, 
            onComplete
        );
    }

    public async sendExplanationQueryRequest(
        request: QueryRequest, 
        onChunk: (chunk: string) => void, 
        onError?: (error: Error) => void, 
        onComplete?: () => void
    ): Promise<void> {
        return await this.PostStreamToApi(
            `${baseEndpoint}/explanation-query`, 
            request, 
            onChunk, 
            onError, 
            onComplete
        );
    }

    public async createProject(projectName: string): Promise<Project> {
        return await this.PostToApi<Project>(`${baseEndpoint}/new-project`, { projectName });
    }

    public async getProjectList(): Promise<ProjectListResponse> {
        return await this.GetFromApi<ProjectListResponse>(`${baseEndpoint}/project-list`);
    }

    public async assignConversationToProject(request: ConversationProjectRequest): Promise<ConversationProjectResponse> {
        return await this.PostToApi<ConversationProjectResponse>(`${baseEndpoint}/assign-conversation-to-project`, request);
    }

    public async getUnderlyingData(conversationId: string, messageId: string, dimensionName?: string): Promise<any> {
        const request = {
            message_id: messageId,
            conversation_id: conversationId,
            ...(dimensionName && { dimension_name: dimensionName })
        };
        return await this.PostToApi<any>(`${baseEndpoint}/underlying-data`, request);
    }

    public async deleteProject(projectId: string): Promise<any> {
        return await this.DeleteFromApi<any>(`${baseEndpoint}/projects/${projectId}`);
    }

    public async renameProject(projectId: string, projectName: string): Promise<any> {
        const request = {
            project_name: projectName
        };
        return await this.PutToApi<any>(`${baseEndpoint}/projects/${projectId}`, request);
    }

    public async deleteConversation(conversationId: string): Promise<any> {
        return await this.DeleteFromApi<any>(`${baseEndpoint}/conversations/${conversationId}`);
    }

    public async getAvailableFilterColumns(messageId: string): Promise<AvailableFilterColumnsResponse> {
        const requestBody = {
            message_id: messageId
        };
        return await this.PostToApi<AvailableFilterColumnsResponse>(`${baseEndpoint}/available-filter-columns`, requestBody);
    }
}
